class JumpMenu extends HTMLElement {
  constructor() {
    super();

    this.links = [];
    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.links = Array.from(this.querySelectorAll("[data-jump-target]"));
    this.highlightActive = this.dataset.highlightActive === "true";

    if (!this.links.length) return;

    this.links.forEach((link) => link.addEventListener("click", this.onClick));

    if (this.highlightActive) {
      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onScroll, { passive: true });
      this.setActiveFromScroll();
    }
  }

  disconnectedCallback() {
    this.links.forEach((link) => link.removeEventListener("click", this.onClick));
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onScroll);
  }

  onClick(event) {
    const link = event.currentTarget;
    const target = this.getTarget(link);

    // No matching section on this page: fall through to the href.
    if (!target) return;

    event.preventDefault();

    const top = window.scrollY + target.getBoundingClientRect().top - this.getOffset();
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (top < window.scrollY) {
      document.querySelector("sticky-header")?.dispatchEvent(new Event("preventHeaderReveal"));
    }

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: reduceMotion ? "auto" : "smooth",
    });

    if (history.replaceState) {
      history.replaceState(null, "", `#${link.dataset.jumpTarget}`);
    }

    this.setActive(link);
  }

  getTarget(link) {
    const id = link.dataset.jumpTarget;
    if (!id) return null;

    try {
      return document.getElementById(id) || document.querySelector(`[name="${CSS.escape(id)}"]`);
    } catch (error) {
      return document.getElementById(id);
    }
  }

  // Sticky elements sit above the target, so scroll a little further up.
  getOffset() {
    const stickyHeader = document.querySelector(
      ".shopify-section-header-sticky:not(.shopify-section-header-hidden), .header-wrapper--sticky"
    );
    const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
    const stickyContainer = this.closest(".jump-menu-section") || this;
    const selfSticky = ["sticky", "fixed"].includes(getComputedStyle(stickyContainer).position);

    return headerHeight + (selfSticky ? this.getBoundingClientRect().height : 0) + 16;
  }

  onScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.setActiveFromScroll();
    });
  }

  setActiveFromScroll() {
    const threshold = window.scrollY + this.getOffset() + 4;
    let current = null;
    let currentTop = -Infinity;

    this.links.forEach((link) => {
      const target = this.getTarget(link);
      if (!target) return;

      const top = window.scrollY + target.getBoundingClientRect().top;

      // The section furthest down the page that has already reached the threshold.
      if (top <= threshold && top >= currentTop) {
        current = link;
        currentTop = top;
      }
    });

    this.setActive(current);
  }

  setActive(activeLink) {
    if (!this.highlightActive || activeLink === this.activeLink) return;

    this.activeLink = activeLink;

    this.links.forEach((link) => {
      if (link === activeLink) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });

    if (activeLink) this.scrollLinkIntoView(activeLink);
  }

  // Keep the active pill visible inside the mobile scroller.
  scrollLinkIntoView(link) {
    const list = this.querySelector(".jump-menu__list");
    if (!list || list.scrollWidth <= list.clientWidth) return;

    const listRect = list.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();

    if (linkRect.left >= listRect.left && linkRect.right <= listRect.right) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    list.scrollTo({
      left: list.scrollLeft + linkRect.left - listRect.left - 20,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }
}

if (!customElements.get("jump-menu")) {
  customElements.define("jump-menu", JumpMenu);
}
